import { create } from 'zustand';
import { storageKey } from '@/config/app';
import type { RorosOperatingMode } from '@/types/roros';

type RorosView = 'ceo' | 'vp' | 'team';

interface RorosState {
  activeView: RorosView;
  activeDomainId: string | null;
  onboardingComplete: boolean;
  operatingMode: RorosOperatingMode | null;
}

interface RorosActions {
  setActiveView: (view: RorosView) => void;
  setActiveDomainId: (domainId: string | null) => void;
  setOnboardingComplete: (complete: boolean) => void;
  setOperatingMode: (mode: RorosOperatingMode | null) => void;
  reset: () => void;
}

const getStoredView = (): RorosView => {
  try {
    const stored = localStorage.getItem(storageKey('roros_active_view'));
    if (stored === 'ceo' || stored === 'vp' || stored === 'team') return stored;
    return 'ceo';
  } catch {
    return 'ceo';
  }
};

const getStoredDomainId = (): string | null => {
  try {
    return localStorage.getItem(storageKey('roros_active_domain_id'));
  } catch {
    return null;
  }
};

const getStoredOnboarding = (): boolean => {
  try {
    return localStorage.getItem(storageKey('roros_onboarding_complete')) === 'true';
  } catch {
    return false;
  }
};

const getStoredOperatingMode = (): RorosOperatingMode | null => {
  try {
    const stored = localStorage.getItem(storageKey('roros_operating_mode'));
    if (stored === 'founder' || stored === 'growth' || stored === 'scaling') {
      return stored as RorosOperatingMode;
    }
    return null;
  } catch {
    return null;
  }
};

export const useRorosStore = create<RorosState & RorosActions>((set) => ({
  activeView: getStoredView(),
  activeDomainId: getStoredDomainId(),
  onboardingComplete: getStoredOnboarding(),
  operatingMode: getStoredOperatingMode(),

  setActiveView: (view: RorosView) => {
    try {
      localStorage.setItem(storageKey('roros_active_view'), view);
    } catch {}
    set({ activeView: view });
  },

  setActiveDomainId: (domainId: string | null) => {
    try {
      if (domainId) {
        localStorage.setItem(storageKey('roros_active_domain_id'), domainId);
      } else {
        localStorage.removeItem(storageKey('roros_active_domain_id'));
      }
    } catch {}
    set({ activeDomainId: domainId });
  },

  setOnboardingComplete: (complete: boolean) => {
    try {
      localStorage.setItem(storageKey('roros_onboarding_complete'), String(complete));
    } catch {}
    set({ onboardingComplete: complete });
  },

  setOperatingMode: (mode: RorosOperatingMode | null) => {
    try {
      if (mode) {
        localStorage.setItem(storageKey('roros_operating_mode'), mode);
      } else {
        localStorage.removeItem(storageKey('roros_operating_mode'));
      }
    } catch {}
    set({ operatingMode: mode });
  },

  reset: () => {
    try {
      localStorage.removeItem(storageKey('roros_active_view'));
      localStorage.removeItem(storageKey('roros_active_domain_id'));
      localStorage.removeItem(storageKey('roros_onboarding_complete'));
      localStorage.removeItem(storageKey('roros_operating_mode'));
    } catch {}
    set({
      activeView: 'ceo',
      activeDomainId: null,
      onboardingComplete: false,
      operatingMode: null,
    });
  },
}));
